import * as React from 'react';
import { BaseInput, Box, styled } from '@leagueplatform/genesis-core';
import { useLocales } from '@web-config-app/core-react';
import { Tag } from '@web-config-app/core-react-ui';
import type { ControlComponent } from '@web-config-app/entity-form';
import { EntityFormControl } from '@web-config-app/entity-form';
import { getPlainTextControlHandlers } from '../../../hooks/plain-text-control/use-plain-text-control.hook';
import { HiddenUnicodeWarning } from '../hidden-unicode/hidden-unicode-warning.component';
import { getInputStatusWithHiddenUnicodeWarning } from '../hidden-unicode/get-input-status-with-hidden-unicode-warning.util';

const TextAreaWrapper = styled('div', {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  flex: 1,
});

export const TextareaControl: ControlComponent = (props) => {
  const { currentLocale, hasMultipleLocales } = useLocales();
  const textInputGroup = React.useRef<HTMLDivElement>(null);
  const setInputFocus = React.useCallback(() => {
    textInputGroup.current?.querySelector('input')?.focus();
  }, []);

  return (
    <EntityFormControl
      {...props}
      renderControl={({
        data,
        handleChange,
        path,
        schema,
        required,
        inputStatus,
        enabled,
        localized,
        id,
      }) => {
        const { maxLength, minLength } = schema;
        const warningMessageId = `${id}-hidden-unicode-warning`;
        const {
          hasHiddenCharacters,
          handleBlur,
          handleChange: handlePlainTextChange,
        } = getPlainTextControlHandlers({
          value: data ?? '',
          path,
          onChange: handleChange,
        });
        const resolvedInputStatus = getInputStatusWithHiddenUnicodeWarning(
          inputStatus,
          hasHiddenCharacters,
        );

        return (
          <>
            <TextAreaWrapper onClick={setInputFocus} ref={textInputGroup}>
              <BaseInput
                as="textarea"
                id={id}
                value={data ?? ''}
                readOnly={!enabled}
                required={required}
                minLength={minLength}
                maxLength={maxLength}
                inputStatus={resolvedInputStatus}
                aria-describedby={
                  hasHiddenCharacters
                    ? `${id}-hint ${warningMessageId}`
                    : `${id}-hint`
                }
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  handlePlainTextChange(e.currentTarget.value)
                }
                onBlur={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  handleBlur(e.currentTarget.value)
                }
                css={{
                  backgroundColor: '$inputBackgroundDefault',
                  position: 'relative',
                  '&:after': {
                    pointerEvents: 'none',
                    content: '"⟷"',
                    position: 'absolute',
                    textAlign: 'center',
                    bottom: '6px',
                    insetInlineEnd: '-2px',
                    transform: 'rotate(90deg)',
                  },
                  '& textarea': {
                    resize: 'vertical',
                    '&::-webkit-resizer': {
                      display: 'none',
                    },
                    paddingInlineEnd: localized ? '$four' : '$one',
                  },
                }}
                rows={4}
              />

              {localized && hasMultipleLocales && (
                <Box
                  className="trailing-content"
                  css={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    paddingInlineEnd: '$one',
                    paddingBlockStart: '$threeQuarters',
                    position: 'absolute',
                    insetInlineEnd: 0,
                    insetBlockStart: 0,
                    pointerEvents: 'auto',
                  }}
                >
                  <Tag label={currentLocale} />
                </Box>
              )}
            </TextAreaWrapper>
            <HiddenUnicodeWarning
              id={id}
              currentValue={data ?? ''}
              path={path}
              enabled={enabled}
              onChange={handleChange}
            />
          </>
        );
      }}
    />
  );
};
